/**
 * Jonah coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const jonahColoringPagesPack: ToilePack = {
    slug: 'jonah-coloring-pages',
    category: 'faith',
    name: 'Jonah coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'storm', label: 'The storm', subject: 'sailors on a tossing ship in a great storm lowering Jonah over the side into the waves'},
        {key: 'fish', label: 'Great fish', subject: 'Jonah praying with folded hands inside the belly of the great fish, seaweed around him'},
        {key: 'nineveh', label: 'Nineveh', subject: 'Jonah preaching at the gates of Nineveh as the people in sackcloth listen, the city walls behind'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['big fish', 'gourd plant', 'sunrise'],
    directionHint: 'try: big fish',
    seo: {
        title: 'Jonah Coloring Pages: The Storm, Great Fish, Nineveh',
        metaDescription: 'Nine Jonah coloring pages for Sunday school from the book of Jonah: the storm at sea, the great fish and Jonah at Nineveh, in three line styles.',
        h1: 'Jonah coloring pages: the storm, the great fish, Nineveh',
        tagline: 'Nine pages of the book of Jonah in three scenes, each in three line weights.',
        intro: 'Printable Jonah coloring pages for Sunday school and Christian homeschool, from the book of Jonah. Three scenes: sailors in a great storm lowering Jonah into the waves, Jonah praying inside the great fish, and Jonah preaching at the gates of Nineveh. Each scene comes in bold lines for young children, classic lines for ages six to nine, and fine detail for older children. Type a different scene or detail, and the same styles carry it.',
        useCases: [
            'a Sunday school lesson on Jonah',
            "a children's church craft activity",
            'a Christian homeschool unit on the minor prophets',
            'a bulletin board on the book of Jonah',
        ],
        faq: [
            {q: 'Which part of Jonah do these show?', a: 'The storm at sea, the great fish, and Jonah preaching at Nineveh.'},
            {q: 'Is the fish a whale?', a: 'The book of Jonah calls it a great fish, and the page follows that wording.'},
            {q: 'Can I ask for a gourd plant or a sunrise?', a: 'Yes. Type the scene you want. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
